import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { fetchTransportCompanies } from '../api/services';
import { useDebounce } from '../hooks';
import { useFetch } from '../hooks';
import {
  EmptyState,
  ErrorDisplay,
  PaginationControl,
  SectionHeader,
  TransportCompanyCard,
  CompanyCardSkeleton,
  TransportCompanyFilterSidebar,
} from '../components/common';
import { DESIGN_TOKENS } from '../styles/theme';
import type { TransportCompanyFilters } from '../types';
import { useI18n } from '../i18n';

export default function TransportCompaniesPage() {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = Number(searchParams.get('page') ?? '1');
    return Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  });
  const [activeFilters, setActiveFilters] = useState<TransportCompanyFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
    pageSize: 12,
  });
  const [isMobileFilterDrawerOpen, setIsMobileFilterDrawerOpen] = useState(false);

  const debouncedSearchQuery = useDebounce(searchInputValue, 400);

  const appliedFilters: TransportCompanyFilters = {
    ...activeFilters,
    searchQuery: debouncedSearchQuery || undefined,
    page: currentPage,
  };

  const { data: companiesData, isLoading, error, refetch } = useFetch(
    () => fetchTransportCompanies(appliedFilters),
    [debouncedSearchQuery, currentPage, activeFilters]
  );

  const handleFiltersChange = useCallback((newFilters: Partial<TransportCompanyFilters>) => {
    setActiveFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const totalPages = companiesData?.meta?.pagination?.pageCount ?? 1;
  const totalItems = companiesData?.meta?.pagination?.total ?? 0;

  useEffect(() => {
    const pageParam = Number(searchParams.get('page') ?? '1');
    const nextPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }
  }, [currentPage, searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (currentPage > 1) {
      nextParams.set('page', String(currentPage));
    } else {
      nextParams.delete('page');
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [currentPage, searchParams, setSearchParams]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(totalPages, 1));
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '60vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <SectionHeader
            title={t('companies.transportCompanies')}
            subtitle={t('companies.pageSubtitle')}
          />
          <Box sx={{ maxWidth: 640 }}>
            <TextField
              fullWidth
              placeholder={t('companies.searchPlaceholder')}
              value={searchInputValue}
              onChange={(event) => setSearchInputValue(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') setCurrentPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: DESIGN_TOKENS.colors.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: DESIGN_TOKENS.colors.surface }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Sidebar */}
          {!isMobileView && (
            <Grid item md={3} lg={2.5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 80,
                  backgroundColor: DESIGN_TOKENS.colors.surface,
                  borderRadius: DESIGN_TOKENS.borderRadius.card,
                  border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`, backgroundColor: DESIGN_TOKENS.colors.primaryLighter }}>
                  <Typography variant="h6">{t('filters.title')}</Typography>
                </Box>
                <TransportCompanyFilterSidebar filters={activeFilters} onFiltersChange={handleFiltersChange} />
              </Box>
            </Grid>
          )}

          {/* Main content */}
          <Grid item xs={12} md={9} lg={9.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? t('common.loading') : t('companies.resultsSummary', { total: totalItems })}
              </Typography>
              {isMobileView && (
                <Button startIcon={<FilterListIcon />} variant="outlined" size="small" onClick={() => setIsMobileFilterDrawerOpen(true)}>
                  {t('filters.title')}
                </Button>
              )}
            </Box>

            {error && <ErrorDisplay message={error} onRetry={refetch} />}

            {!error && (
              <Grid container spacing={2.5}>
                {isLoading
                  ? Array.from({ length: 12 }).map((_, skeletonIndex) => (
                      <Grid item xs={12} sm={6} xl={4} key={`skeleton-${skeletonIndex}`}>
                        <CompanyCardSkeleton />
                      </Grid>
                    ))
                  : companiesData?.data?.map((company) => (
                      <Grid item xs={12} sm={6} xl={4} key={company.id}>
                        <TransportCompanyCard company={company} />
                      </Grid>
                    ))}
              </Grid>
            )}

            {!isLoading && !error && companiesData?.data?.length === 0 && (
              <EmptyState
                title={t('companies.noCompaniesTitle')}
                description={t('companies.noCompaniesDescription')}
                actionLabel={t('filters.clear')}
                onAction={() => {
                  setSearchInputValue('');
                  setActiveFilters({ sortBy: 'createdAt', sortOrder: 'desc', pageSize: 12 });
                  setCurrentPage(1);
                }}
              />
            )}

            <PaginationControl currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={activeFilters.pageSize} onPageChange={setCurrentPage} />
          </Grid>
        </Grid>
      </Container>

      <Drawer
        anchor="bottom"
        open={isMobileFilterDrawerOpen}
        onClose={() => setIsMobileFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' } }}
      >
        <TransportCompanyFilterSidebar filters={activeFilters} onFiltersChange={handleFiltersChange} onClose={() => setIsMobileFilterDrawerOpen(false)} />
      </Drawer>
    </Box>
  );
}
